import React, { useState } from 'react'
import {
    Button,
    Icon,
    Card,
    DropdownMenu,
} from 'components'
import * as fnc from 'helpers/fnc'
import { QueryType, AnalyticsFilter } from 'app/types'
import { useAppSelector, useAppDispatch } from 'app/hooks'
import { applyQuery } from 'actions/appActions'
import { setQueryHelper } from 'helpers/query'
import { logger } from 'helpers/logger'

interface WizardStep {
    id: string
    title: string
    description: string
    icon?: string
    options: WizardOption[]
}

interface WizardOption {
    id: string
    title: string
    description?: string
    icon?: string
    filters?: Record<string, any>
    columns?: string[]
    next?: string // ID of the next step
    aggregation?: string // Key from the definitions object to set in the table
    recent?: boolean // Mark as recently added
    badge?: string // Badge text/icon to display (e.g., ⭐)
}

export interface WizardTemplate {
    id: string
    title: string
    description: string
    initialStep: string
    steps: WizardStep[]
}

interface AnalyticsWizardProps {
    template: WizardTemplate
    onComplete: (selectedOptions: Record<string, WizardOption>, aggregation?: string) => void
    onCancel?: () => void
}

export function AnalyticsWizard(props: AnalyticsWizardProps) {
    const { template, onComplete, onCancel } = props
    const dispatch = useAppDispatch()

    // Track current step and selected options
    const [currentStepId, setCurrentStepId] = useState(template.initialStep)
    const [selectedOptions, setSelectedOptions] = useState<Record<string, WizardOption>>({})

    // Track the path of steps taken
    const [stepPath, setStepPath] = useState<string[]>([template.initialStep])

    // Get current step
    const currentStep = template.steps.find(step => step.id === currentStepId)

    // Helper to get the final aggregation from selected options
    const getFinalAggregation = (options: Record<string, WizardOption>): string | undefined => {
        // Start from the most recent selection and work backwards
        const optionValues = Object.values(options).reverse()
        for (const option of optionValues) {
            if (option.aggregation) {
                return option.aggregation
            }
        }
        return undefined
    }

    // Handle option selection
    const handleOptionSelect = (option: WizardOption) => {
        // Store the selected option
        const newSelectedOptions = {
            ...selectedOptions,
            [currentStepId]: option
        }
        setSelectedOptions(newSelectedOptions)

        // Apply filters immediately
        if (option.filters) {
            Object.entries(option.filters).forEach(([filterKey, filterValue]) => {
                // Get the current query state
                const query = useAppSelector(state => state.app.queries[QueryType.Analytics]) || {}

                // Set the query
                const newQuery = setQueryHelper(QueryType.Analytics, query, { [filterKey]: filterValue })
                dispatch(applyQuery({ query: newQuery, type: QueryType.Analytics }))
            })
        }

        // Navigate to next step or finish
        if (option.next) {
            // Add the next step to our path
            let newPath = [...stepPath]

            // If we're navigating to a step we've already visited, truncate the path
            const existingStepIndex = newPath.indexOf(option.next)
            if (existingStepIndex !== -1) {
                newPath = newPath.slice(0, existingStepIndex + 1)
            } else {
                // Otherwise add the new step to our path
                newPath.push(option.next)
            }

            setStepPath(newPath)
            setCurrentStepId(option.next)
        } else {
            // No next step means we're done
            // Find the final aggregation value - first from this option, then search through all selected options
            const aggregation = option.aggregation || getFinalAggregation(newSelectedOptions)

            logger.info(`Completing wizard with aggregation: ${aggregation}`)
            onComplete(newSelectedOptions, aggregation)
        }
    }

    // Render options for the current step
    const renderOptions = () => {
        if (!currentStep) return null

        let perRow = 4
        switch (currentStep.options.length) {
            case 1:
            case 2:
                perRow = 2
                break
            case 3:
                perRow = 3
                break
            default:
                perRow = 4
        }

        return (
            <div className="wizard-options" >
                {currentStep.options.map((option) => (
                    <Card
                        key={option.id}
                        className="wizard-option-card"
                        onClick={() => handleOptionSelect(option)}
                    >
                        {option.icon && <Icon noBg icon={option.icon} className="wizard-option-icon" />}
                        <h3>{option.title} {option.badge && <span className="wizard-badge">{option.badge}</span>}</h3>
                        {option.description && <p>{option.description}</p>}
                    </Card>
                ))}
            </div>
        )
    }

    // Back button handler
    const handleBack = () => {
        // Find which step is before the current one in our path
        const currentStepIndex = stepPath.indexOf(currentStepId)
        if (currentStepIndex > 0) {
            const previousStepId = stepPath[currentStepIndex - 1]

            // Remove the current selection
            const { [currentStepId]: _, ...remainingOptions } = selectedOptions
            setSelectedOptions(remainingOptions)

            // Update the step path to remove all steps after the previous step
            const newStepPath = stepPath.slice(0, currentStepIndex)
            setStepPath(newStepPath)

            // Navigate to previous step
            setCurrentStepId(previousStepId)
        }
    }

    // Check if we're on the first step
    const isFirstStep = currentStepId === template.initialStep

    // Get current step position in the path
    const currentStepIndex = stepPath.indexOf(currentStepId)

    return (
        <div className="analytics-wizard">
            <div className="wizard-header">
                <h2>{template.title}</h2>
                {template.description && <p>{template.description}</p>}
            </div>
            <div className="wizard-step">
                {/* <h3>{currentStep?.title}</h3>
                {currentStep && <p>{currentStep?.description}</p>} */}
                {renderOptions()}
            </div>
            <div className="wizard-footer">
                <div className="button-group">
                    {!isFirstStep && (
                        <Button onClick={handleBack} icon="fas fa-arrow-left">
                            Back
                        </Button>
                    )}
                    {onCancel && (
                        <Button onClick={onCancel}>
                            Cancel
                        </Button>
                    )}
                </div>
            </div>
        </div>
    )
}